import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { DeckBuilderHost, openFromButton } from './deck-builder/DeckBuilderModal';

/**
 * Lane 1405 v3 (PLAN_v3_actions.md section 1, Ryan's approved mockup): the
 * Build a deck and Build a workbook buttons by the chat box.
 *
 * Each is shown when its feature is on for the company, and disabled with a
 * one-line hint until the chat has a completed answer. A click acts on the
 * chat's newest answer and adds nothing to the chat by itself:
 * - Build a deck opens the deck builder at once (POST /api/deck-builder-requests);
 * - Build a workbook gets a signed offer for that answer and the answer's turn
 *   id (POST /api/report-offers/latest) and hands both to the page, which puts
 *   the offer on that answer and starts the existing one-click workbook flow
 *   (`onWorkbookOffer`).
 * "That answer" is the server's newest ordinary answer: a reply that only
 * started a deck is not one.
 * A refusal from the server is shown as its own plain line.
 */
const NO_ANSWER_HINT = 'Ask Wingman about a supplier first';
const ALREADY_STARTED = 'The workbook for this answer is already in the chat.';

async function detailMessage(response, fallback) {
  const value = await response.json().catch(() => null);
  const detail = value?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.message || fallback;
}

export default function ChatActionButtons({ deckEnabled, workbookEnabled, conversationId, authFetch,
  hasAnswer, disabled, onWorkbookOffer, workbookStarting = false }) {
  const [deckId, setDeckId] = useState(null);
  const [busy, setBusy] = useState(null);       // 'deck' | 'workbook' | null
  const [message, setMessage] = useState('');
  if (!deckEnabled && !workbookEnabled) return null;
  const off = disabled || !hasAnswer || busy !== null;

  const buildDeck = async () => {
    setMessage(''); setBusy('deck');
    try { setDeckId(await openFromButton(authFetch, conversationId)); }
    catch (failure) { setMessage(failure.message); }
    finally { setBusy(null); }
  };
  const buildWorkbook = async () => {
    setMessage(''); setBusy('workbook');
    try {
      const response = await authFetch('/api/report-offers/latest', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: conversationId }) });
      if (!response.ok) throw new Error(await detailMessage(response, 'The workbook could not be started.'));
      const value = await response.json();
      // The offer names its answer, so it lands on that answer even when the
      // chat has moved on while it was on its way (Codex S4 r1).
      const outcome = onWorkbookOffer(value.report_offer, value.answer_turn_id);
      if (outcome === 'already') setMessage(ALREADY_STARTED);
      else if (outcome !== 'started') setMessage('That answer is no longer in this chat. Click Build a workbook again.');
    } catch (failure) { setMessage(failure.message); }
    finally { setBusy(null); }
  };

  return <div className="hm-actions" data-testid="hm-chat-actions">
    {deckEnabled && <button type="button" className="hm-action" disabled={off} onClick={buildDeck}
      title={hasAnswer ? undefined : NO_ANSWER_HINT}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
        strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
      {busy === 'deck' ? 'Opening…' : 'Build a deck'}</button>}
    {workbookEnabled && <button type="button" className="hm-action" disabled={off || workbookStarting}
      onClick={buildWorkbook}
      title={hasAnswer ? undefined : NO_ANSWER_HINT}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
        strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M3 15h18M9 3v18" /></svg>
      {busy === 'workbook' || workbookStarting ? 'Starting…' : 'Build a workbook'}</button>}
    {!hasAnswer && <span className="hm-action-hint">{NO_ANSWER_HINT}</span>}
    {message && <span className="hm-action-msg" role="alert">{message}</span>}
    {/* Outside the chat form, so Enter in the pop-up never sends the chat (Codex S4 r1). */}
    {deckId && createPortal(<DeckBuilderHost publicId={deckId} authFetch={authFetch} conversationId={conversationId}
      onClose={() => setDeckId(null)} />, document.body)}
  </div>;
}
